/**
 * pages/LoginPage.tsx — CLI-450
 * Вход пациента по телефону + OTP. Мобайл-first дизайн.
 * Шаг 1: ввод clinicId + телефона → запрос кода.
 * Шаг 2: ввод 6-значного кода → получение токенов → переход в кабинет.
 */
import clincoreLogo from '../assets/clincore-logo.svg';
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { requestCode, verifyCode } from '../services/patient.service';
import { useAuthStore } from '../store/auth.store';

export function LoginPage() {
  const { patient, setAuth } = useAuthStore();
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (patient) return <Navigate to="/portal" replace />;

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!phone.trim() || !clinicId.trim()) {
      setError('Укажите ID клиники и номер телефона');
      return;
    }
    setLoading(true);
    try {
      const result = await requestCode(phone.trim(), clinicId.trim());
      setDevCode(result._devCode ?? null);
      setStep('code');
    } catch {
      setError('Ошибка отправки кода. Проверьте данные и повторите.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError('Введите 6-значный код');
      return;
    }
    setLoading(true);
    try {
      const result = await verifyCode(phone.trim(), clinicId.trim(), code.trim());
      setAuth(result.accessToken, result.refreshToken, result.patient, clinicId.trim());
    } catch {
      setError('Неверный код или истёк срок действия. Попробуйте снова.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-700 to-primary-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={clincoreLogo} alt="" className="inline-block w-16 h-16 rounded-2xl mb-4 shadow-lg" />
          <h1 className="text-2xl font-bold text-white">Мой кабинет</h1>
          <p className="text-primary-200 text-sm mt-1">ClinCore · Личный кабинет пациента</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6">
          {step === 'phone' ? (
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Вход по номеру телефона</h2>
              <form onSubmit={(e) => { void handleRequestCode(e); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID клиники
                  </label>
                  <input
                    type="text"
                    value={clinicId}
                    onChange={(e) => setClinicId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Номер телефона
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+7 (999) 123-45-67"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoComplete="tel"
                    disabled={loading}
                  />
                </div>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition"
                >
                  {loading ? 'Отправка…' : 'Получить код'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Введите код</h2>
              <p className="text-sm text-gray-500 mb-4">
                Код отправлен на номер <strong>{phone}</strong>. Действителен 10 минут.
              </p>
              {devCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-center">
                  <p className="text-xs text-amber-700 font-medium">DEV-режим · код:</p>
                  <p className="text-2xl font-mono font-bold text-amber-800 tracking-widest">{devCode}</p>
                </div>
              )}
              <form onSubmit={(e) => { void handleVerifyCode(e); }} className="space-y-4">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full text-center text-3xl font-mono tracking-[0.5em] border border-gray-300 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                  disabled={loading}
                />
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition"
                >
                  {loading ? 'Проверка…' : 'Войти'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('phone'); setCode(''); setError(null); setDevCode(null); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Изменить телефон
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-primary-200 text-xs mt-6">
          Ваши данные защищены в соответствии с ФЗ-152
        </p>
      </div>
    </div>
  );
}
